// AI 支援（「ヒントをもらう」）の画面の仕組み。Web とモバイルで共通。
// 方針：本人が押したときだけ動く／候補は「使う」を押すと入力欄に入るだけで、保存は別の操作／
// 無効・上限・混雑のときは、理由と、いつ使えるかを示し、手入力は常に使える。
import type { AssistRequestBody, AssistResult, AssistJobStatus, AssistSubmitResponse } from "../../api/futureBoard";

export type AssistMode = AssistRequestBody["mode"];
export type AssistField = "title" | "meaning" | "self_image" | "scene";

export const POLISH_FIELDS: readonly AssistField[] = ["title", "meaning", "self_image", "scene"];
// サーバーが、1回に受け取る・使う量（services/futureBoard/assist）。ここを超える入力は、送る前に断る。
export const POLISH_MAX_CHARS = 800;
export const MAX_ANSWERS = 6;

// 編集の段階（0〜3）ごとの、既定のモード。写真と言葉（3）は、文章を整える。
export function modeForStep(step: number): AssistMode {
  return (["explore", "self_image", "scene", "polish"] as const)[Math.min(Math.max(step, 0), 3)];
}

// 候補が入る項目。explore は候補を返さない。
export function fieldForMode(mode: AssistMode, polishField: AssistField = "self_image"): AssistField | null {
  if (mode === "self_image") return "self_image";
  if (mode === "scene") return "scene";
  if (mode === "polish") return polishField;
  return null;
}

export interface AssistDraft {
  title: string;
  meaning: string;
  self_image: string;
  scene: string;
  sensations: string[];
}

export interface AssistAnswer {
  question: string;
  answer: string;
}

const length = (value: string) => [...value.trim()].length;

// 押せるか、押せないなら理由（i18n のキー）。
export function assistBlocker(mode: AssistMode, draft: AssistDraft, field: AssistField | null): string | null {
  if ((mode === "self_image" || mode === "scene") && length(draft.title) === 0) return "futureBoard.assist.needTitle";
  if (mode === "polish") {
    const text = field ? draft[field] : "";
    if (!field || length(text) === 0) return "futureBoard.assist.polishEmpty";
    if (length(text) > POLISH_MAX_CHARS) return "futureBoard.assist.polishTooLong";
  }
  return null;
}

// サーバーへ送る内容。いま入力している内容（未保存の分を含む）を、依頼の対象として渡す。保存はしない。
export function buildAssistRequest(input: {
  mode: AssistMode; language: string; cardId: number | null; draft: AssistDraft; field?: AssistField | null;
  answers?: readonly AssistAnswer[]; note?: string;
}): AssistRequestBody {
  const body: AssistRequestBody = { mode: input.mode, language: input.language === "en" ? "en" : "ja" };
  if (input.cardId !== null) body.card_id = input.cardId;
  const { title, meaning, self_image, scene, sensations } = input.draft;
  body.draft = input.mode === "explore" ? { title } : { title, meaning, self_image, scene, sensations };
  if (input.mode === "polish" && input.field) body.field = input.field;
  if (input.mode === "explore" && input.answers?.length) body.answers = input.answers.slice(-MAX_ANSWERS).map(item => ({ question: item.question, answer: item.answer }));
  const note = input.note?.trim();
  if (note && input.mode === "scene") body.note = note;
  return body;
}

// 「使う」：候補を、その項目の入力欄に入れる（置き換える）。保存はしない。
export function applyCandidate<T extends AssistDraft>(draft: T, field: AssistField, text: string): T {
  return { ...draft, [field]: text };
}

// --- 結果の確認（ポーリング） ---

export const POLL_TIMEOUT_MS = 120_000;
// 2秒以上あけ、待つほど間隔を延ばす（サーバーに、ポーリングで負荷をかけない）。
export const pollDelay = (attempt: number): number => Math.min(5000, 2000 + Math.max(0, attempt - 2) * 1000);

export type AssistOutcome =
  | { outcome: "result"; result: AssistResult; cached: boolean }
  | { outcome: "failed"; code: string }
  | { outcome: "timeout" }
  | { outcome: "cancelled" };

export interface AssistRunnerDeps {
  submit: (body: AssistRequestBody) => Promise<AssistSubmitResponse>;
  poll: (jobId: string) => Promise<AssistJobStatus>;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  isCancelled?: () => boolean;
  timeoutMs?: number;
}

// 依頼を送り、結果が出るまで待つ。通信の失敗（上限・混雑・無効など）は、そのまま例外として投げる
// （parseFutureBoardError で種類に分ける）。同じ入力の再利用（cached）は、待たずに返る。
export async function runAssist(body: AssistRequestBody, deps: AssistRunnerDeps): Promise<AssistOutcome> {
  const sleep = deps.sleep ?? (ms => new Promise<void>(resolve => setTimeout(resolve, ms)));
  const now = deps.now ?? Date.now;
  const timeoutMs = deps.timeoutMs ?? POLL_TIMEOUT_MS;
  const cancelled = deps.isCancelled ?? (() => false);

  const first = await deps.submit(body);
  if ("cached" in first && first.cached) return { outcome: "result", result: first.result, cached: true };
  const jobId = String((first as { job_id: string }).job_id);

  const started = now();
  for (let attempt = 0; ; attempt++) {
    if (cancelled()) return { outcome: "cancelled" };
    await sleep(pollDelay(attempt));
    if (cancelled()) return { outcome: "cancelled" };
    if (now() - started > timeoutMs) return { outcome: "timeout" };
    const status = await deps.poll(jobId);
    if (status.status === "completed") return { outcome: "result", result: status.result, cached: false };
    if (status.status === "failed") return { outcome: "failed", code: status.error };
  }
}

// ジョブの失敗の種類（サービスの AI_*）を、画面の文言のキーにする。知らない種類は、一般の失敗に。
const KNOWN_FAILURES = new Set(["AI_UNAVAILABLE", "AI_TIMEOUT", "AI_INVALID_OUTPUT", "AI_CARD_NOT_FOUND", "AI_FAILED"]);
export const assistFailureKey = (code: string): string => `futureBoard.errors.${KNOWN_FAILURES.has(code) ? code : "AI_FAILED"}`;

// --- 画面の状態（reducer） ---

export type AssistPhase = "idle" | "working" | "question" | "candidates" | "none" | "error";

export interface AssistState {
  phase: AssistPhase;
  mode: AssistMode | null;
  field: AssistField | null;
  answers: AssistAnswer[];
  question: string | null;
  candidates: string[];
  /** 本人が書いていない内容を含む、想像上の候補（scene） */
  imagined: boolean;
  used: number[];
  messageKey: string | null;
  /** AI_TOO_SOON のあと、あと何秒待つか */
  waitSeconds: number | null;
  resetsAt: string | null;
}

export const initialAssistState: AssistState = {
  phase: "idle", mode: null, field: null, answers: [], question: null, candidates: [], imagined: false,
  used: [], messageKey: null, waitSeconds: null, resetsAt: null,
};

export type AssistAction =
  | { type: "start"; mode: AssistMode; field: AssistField | null }
  | { type: "result"; result: AssistResult }
  | { type: "failed"; messageKey: string; waitSeconds?: number | null; resetsAt?: string | null }
  | { type: "answer"; text: string }
  | { type: "use"; index: number }
  | { type: "close" };

export function assistReducer(state: AssistState, action: AssistAction): AssistState {
  switch (action.type) {
    case "start": {
      // 問い（explore）は、これまでの答えを引き継ぐ。ほかのモードに変えたら、答えは捨てる。
      const keep = action.mode === "explore" && state.mode === "explore" ? state.answers : [];
      return { ...initialAssistState, phase: "working", mode: action.mode, field: action.field, answers: keep };
    }
    case "result": {
      const { result } = action;
      if (result.kind === "question") return { ...state, phase: "question", question: result.question, candidates: [], used: [], messageKey: null };
      if (result.kind === "candidates") {
        return { ...state, phase: "candidates", field: result.field, candidates: result.candidates, question: null, used: [], imagined: state.mode === "scene", messageKey: null };
      }
      return { ...state, phase: "none", candidates: [], question: null, messageKey: null };
    }
    case "failed":
      return { ...state, phase: "error", messageKey: action.messageKey, waitSeconds: action.waitSeconds ?? null, resetsAt: action.resetsAt ?? null };
    case "answer": {
      const text = action.text.trim();
      if (state.question === null || !text) return state;
      return { ...state, answers: [...state.answers, { question: state.question, answer: text }].slice(-MAX_ANSWERS) };
    }
    case "use":
      return state.used.includes(action.index) ? state : { ...state, used: [...state.used, action.index] };
    case "close":
      return { ...initialAssistState };
    default:
      return state;
  }
}
